import { DataTable } from '@app/components/data-table/data-table';
import { useLocalDataTable } from '@app/components/data-table/hooks/use-local-data-table';
import { IngredientResult, ProductResult } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import { useMemo, useState } from 'react';
import ProductDetailIngredientSheet from './product-detail-ingredient-sheet';
import { useProductIngredientsTableColumns } from './product-ingredients-table-columns';

const PAGE_SIZE = 10;

function IngredientsDataTable({
  data,
  maxEmissions,
  onRowClick,
}: {
  data: IngredientResult[];
  maxEmissions: number;
  onRowClick: (row: IngredientResult | undefined) => void;
}) {
  const columns = useProductIngredientsTableColumns(maxEmissions);
  const { table } = useLocalDataTable({
    data,
    columns,
    getRowId: (row: IngredientResult) => row.uuid,
  });
  return (
    <DataTable
      table={table}
      pagination={false}
      className="bg-gray-00 text-left shadow-sm"
      tableClassName="table-fixed"
      columnClassName={{ emissions: 'border-r', bar: 'pl-0 mr-0' }}
      onRowClick={(row) => onRowClick(row?.original)}
      rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
    />
  );
}

interface ProductIngredientsTableProps {
  ingredients: readonly IngredientResult[];
  maxEmissions: number;
  product: ProductResult;
}

export default function ProductIngredientsTable({
  product,
  ingredients,
  maxEmissions,
}: ProductIngredientsTableProps) {
  const [showAll, setShowAll] = useState(false);
  const [selectedIngredient, setSelectedIngredient] =
    useState<IngredientResult>();

  const sortedIngredients = useMemo(() => {
    return [...ingredients].sort((a, b) => b.emissions - a.emissions);
  }, [ingredients]);

  const visibleIngredients = showAll
    ? sortedIngredients
    : sortedIngredients.slice(0, PAGE_SIZE);

  return (
    <div>
      <IngredientsDataTable
        key={visibleIngredients.length}
        data={visibleIngredients}
        maxEmissions={maxEmissions}
        onRowClick={setSelectedIngredient}
      />
      {sortedIngredients.length > PAGE_SIZE && (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show less'
            : `Show ${sortedIngredients.length - PAGE_SIZE} more`}
        </Button>
      )}
      {selectedIngredient && (
        <ProductDetailIngredientSheet
          product={product.name}
          isOpen={!!selectedIngredient}
          setIsOpen={(open) => {
            if (!open) setSelectedIngredient(undefined);
          }}
          ingredient={selectedIngredient}
          totalEmissions={product.totalEmissions!}
        />
      )}
    </div>
  );
}
